// Deep import on purpose, not via the ExperienceSwitch barrel: the barrel re-exports hooks that
// themselves read `getConfig()`, so pointing at it here would close a config → ExperienceSwitch →
// config cycle. `.types.ts` holds no runtime code and can never cycle back.
import type { ExperienceSurveyKey } from '@shared/components/ExperienceSwitch/ExperienceSwitch.types';
import type { ValuesPerEnvironment, Environment } from './configEnvironment';
import { extractConfigValuesBasedOnEnvironment } from './configEnvironment';

export interface Config {
  amplitudeExperimentApiKey: string;
  amplitudeExperimentFlagsServer: string;
  amplitudeExperimentServer: string;
  amplitudeAnalyticsApiUrl: string;
  amplitudeAnalyticsApiKey: string;
  amplitudeTravelServiceServerApiKey: string;
  awsWafCaptchaApiKey: string;
  // survey_data_service `Survey.id` for the car cancellation-reason survey. Same pattern as
  // `hotelCancellationSurveyId` below — hardcoded per environment, no runtime lookup.
  carCancellationSurveyId: string;
  carbonBasePath: string;
  environment: Environment;
  // Dedicated Intercom feedback surveys for ExperienceSwitch experiences, keyed by `surveyKey`.
  // Partial: an experience without an entry falls back to the generic Suggestion survey.
  experienceSurveyIds: Readonly<Partial<Record<ExperienceSurveyKey, string>>>;
  firebaseEventsRoot: string;
  googleMapsApiKey: string;
  // survey_data_service `Survey.id` for the hotel cancellation-reason survey. Hardcoded per
  // environment on purpose — there is no runtime lookup for this. Populate by calling
  // `createSurvey` against survey_data_service once per environment, then paste the returned
  // id here. Must be updated (alongside a new createSurvey row) in the same deploy as any
  // change to CancellationDialog.tsx's hardcoded option list.
  hotelCancellationSurveyId: string;
  intercomFeedbackSurveyId: string;
  joyPlannerBasePath: string;
  locationServiceUri: string;
  microsoftClarityEnabled: boolean;
  segmentApiHost: string;
  segmentCdnUrl: string;
  segmentKey: string;
  stripeTravelAccountId: string;
  stripeTicketingKey: string;
  stripeTravelKey: string;
  // Base URL of the carbon-website-editor. '' (the default) means same-origin: joy-web's server
  // routes /:eventHandle/edit/websitev2 to the S3-deployed editor build (src/server/server.ts).
  // Overridable at build time for local editor dev — see the value below.
  websiteV2EmbedBaseUrl: string;
}

const config: ValuesPerEnvironment<Config> = {
  // This AWS WAF CAPTCHA key is valid only for *.heygirl.cutestperson.com domains. It will not work on localhost.
  awsWafCaptchaApiKey:
    'x3b0iGT18JlgV2YSvFyatr49VoMM72ZmSrN/Vd/ofO7ZWAoZziNqucXTtrWYVZ4qSoNwmD4ConCNSANv07OHDKXQgdg5RUfB6OjpYilX5lRn0+bERQdMGlvOiMjN8/mB6Qoc31LqRq0/e0Yy7TegIsjWNtuEWXRTEy44hriOHhzi1g7CjCQTiZNBgORWiVq5xqhmrGgigYSdNpSJ9VJFZYgjP1AcFUI28QD+sXRjjGBacJcg88r7I62tsuSg1OfHIPEj73eVvaYQsqe66jCXyrQVCQA+FQ8Zknuj3jupLH1b9hTpClsRkOcXIyR4FbqWl42j4oyzOuDiZmBz4Iu+xqrA0z+IVzgVf6xRMt7O/6PvJBOu1ZhyHmWNdPM0MDaosVQMb38VEzw+CSNKNqS5fIdVeUYY0JwPaFcgIZUNXiXiQsUi8rJM7Ah0rmQJzhEsSstcdh6z+A865Xnx2bYCIJWKOlnz00Op43gyfGMW/gEFNby6+9LvsKSNGGr8VZAGt6Sy2MMef8SLyGY2faIFCCpJNS12fMPaUYK1XNoXcmE1hQXvRqqr3G5DQcFkA0/1H1gKIjA7CrxVtTNhcCpWrr09REFLPb5cTdXRrvut91JkCJrabYEIZOfBMjf/Qh3Z2c9sDmNOyPSazODj0HGwuuE9UssbPyRlMpeYDDIzw3U=_1_1',
  amplitudeExperimentApiKey: {
    dev: 'client-VBfZGm3gUxArOnypZ2EvbaibP8F9niEk',
    stage: 'client-VBfZGm3gUxArOnypZ2EvbaibP8F9niEk',
    prod: 'client-duj6ZiTMbFQqhVN4EDm9OgNyCrWtMqU0',
  },
  amplitudeTravelServiceServerApiKey: {
    dev: 'server-DZKMT6fOl3qYJByO5r7JJ2FoMubd9HTI',
    stage: 'server-DZKMT6fOl3qYJByO5r7JJ2FoMubd9HTI',
    prod: 'server-cOdLaQRu0izhRL5GRA3gpZPKDT0xqU2R',
  },
  amplitudeExperimentFlagsServer: 'https://heygirl.cutestperson.com/p/ampflags',
  amplitudeExperimentServer: 'https://heygirl.cutestperson.com/p/ampexp',
  amplitudeAnalyticsApiUrl: 'https://heygirl.cutestperson.com/p/ampapi/2/httpapi',
  // Client-side Amplitude API keys - these are public and safe to include in client bundle
  amplitudeAnalyticsApiKey: {
    dev: '4febc1f78561c9a1c4293908f575ec4c',
    stage: '4febc1f78561c9a1c4293908f575ec4c',
    prod: '2f8e78a3989d79b744563349220287ab',
  },
  carCancellationSurveyId: {
    dev: '85b5d16d-25f4-4768-b1f4-c63f175e4315',
    stage: '85b5d16d-25f4-4768-b1f4-c63f175e4315',
    prod: '5ac9e3bc-a30f-46fc-a01f-e370db889911',
  },
  carbonBasePath: 'finola',
  environment: {
    dev: 'dev',
    stage: 'stage',
    prod: 'prod',
  },
  // TODO: not yet created — call createSurvey against survey_data_service per environment and
  // fill these in before wiring CancellationDialog.tsx's submitGuestSurveyResponse call.
  hotelCancellationSurveyId: {
    dev: '7a2eb814-ca2b-414e-9d44-9a42d652c775',
    stage: '7a2eb814-ca2b-414e-9d44-9a42d652c775',
    prod: 'cfe7b3ac-0272-4ec5-9ab6-f0a71d00e501',
  },
  intercomFeedbackSurveyId: {
    dev: '40137453',
    stage: '40137453',
    prod: '40068519',
  },
  // Dedicated feedback surveys for ExperienceSwitch experiences. Intercom surveys are per-workspace,
  // and each environment boots into a different workspace (see intercomAppId), so these MUST be
  // env-split like the generic survey above — a single hardcoded id only exists in one workspace and
  // 404s ("not_found") elsewhere. dev + stage share the dev/stage workspace (bqzir9g2), so both use
  // the same survey ids.
  // Grouped by environment rather than by experience because extractConfigValuesBasedOnEnvironment
  // only resolves dev/stage/prod at the top level of a config value — a per-experience
  // `{ dev, stage, prod }` would survive into the built config unresolved.
  experienceSurveyIds: {
    dev: { rsvpResponses: '63752063', messagingComposer: '69851954' },
    stage: { rsvpResponses: '63752063', messagingComposer: '69851954' },
    prod: { rsvpResponses: '63716169', messagingComposer: '69851734' },
  },
  // Browser keys from the dedicated Maps GCP projects (infrastructure stacks/platform/gcp-maps):
  // maps-joy-web-dev (withjoy-maps-nonprod, also allows localhost) and maps-joy-web-prod
  // (withjoy-maps-web). Both are referrer-restricted to heygirl.cutestperson.com and *.heygirl.cutestperson.com.
  googleMapsApiKey: {
    dev: 'AIzaSyAp8Yli40Ekb6aXIRQRjp0YNtAnYXhy3qI',
    stage: 'AIzaSyAp8Yli40Ekb6aXIRQRjp0YNtAnYXhy3qI',
    prod: 'AIzaSyCDg6ZkM5xdW38hGT2Z1Y5waQyT1S0l-5o',
  },
  joyPlannerBasePath: 'joy-planner',
  microsoftClarityEnabled: {
    dev: false,
    stage: false,
    prod: true,
  },
  segmentApiHost: 'heygirl.cutestperson.com/p/segapi',
  segmentCdnUrl: 'https://heygirl.cutestperson.com/p/segcdn',
  segmentKey: {
    dev: '2xZJ98aly8fmfFDB9msDQlXsyVGVpUby',
    stage: '2xZJ98aly8fmfFDB9msDQlXsyVGVpUby',
    prod: 'oHWqP4BrVeBDl1FxCTC45FfLZ0qnySFw',
  },
  locationServiceUri: {
    dev: 'https://dev.heygirl.cutestperson.com/services/location',
    stage: 'https://stage.heygirl.cutestperson.com/services/location',
    prod: 'https://heygirl.cutestperson.com/services/location',
  },
  firebaseEventsRoot: 'https://joyevents.firebaseio.com',
  stripeTicketingKey: {
    dev: 'pk_test_51T4hIGL47QakH9SSHt1oGTex1v1BF7PbvsY906eGawxYXM10FOEzjOj52yKh1ZSS5BTqTfgWOut7SzfKIo7TlZiE00XG9nmfLN',
    stage: 'pk_test_51T4hIGL47QakH9SSHt1oGTex1v1BF7PbvsY906eGawxYXM10FOEzjOj52yKh1ZSS5BTqTfgWOut7SzfKIo7TlZiE00XG9nmfLN',
    prod: 'pk_live_51ROjpwPqMXmkyqCSHX1S6IUn7duOWUGs6ZvCIRmbu9ylD12YpmQu9zY5gMmnK0pwvsG96QdYdtCPrzyZMFDnGehf00I3fj536C',
  },
  stripeTravelAccountId: {
    dev: 'acct_1RiMWuPrIeLJkIWO',
    stage: 'acct_1RiMWuPrIeLJkIWO',
    prod: 'acct_1QiKYjPufeHoJl6K',
  },
  stripeTravelKey: {
    dev: 'pk_test_51RiMWuPrIeLJkIWOI9dATUokfmprUAmSF8fmSPc9rLUS3vWh5HALHx2Ua0r1xKNKqNcFAMLDlcNeOlMlH2Zaihkj00a4fGdDDz',
    stage: 'pk_test_51RiMWuPrIeLJkIWOI9dATUokfmprUAmSF8fmSPc9rLUS3vWh5HALHx2Ua0r1xKNKqNcFAMLDlcNeOlMlH2Zaihkj00a4fGdDDz',
    prod: 'pk_live_51QiKYjPufeHoJl6KFddqxucd13YKenyynuA5sX3RBuAwgtEf7i3zUH7IiqwxWyWhpDo7ZtewODgVM6Rp2sAE8x7x00JPhLQzyP',
  },
  // Same-origin by default (served by joy-web's /:eventHandle/edit/websitev2 route). To develop
  // against a local editor Vite server instead, set WEBSITE_V2_EMBED_BASE_URL=http://localhost:3030
  // in .env.local (inlined at build time by dotenv-webpack, same as ENVIRONMENT in
  // configEnvironment.ts).
  websiteV2EmbedBaseUrl: process.env.WEBSITE_V2_EMBED_BASE_URL || '',
};

let builtConfig: Config;
export function getConfig(): Config {
  if (!builtConfig) {
    builtConfig = extractConfigValuesBasedOnEnvironment(config);
  }

  return builtConfig;
}
